"use client";

import { OptimizedAvatar } from "@/components/ui/OptimizedAvatar";
import { PublicUser } from "@/types";
import { useTranslations } from "next-intl";

interface TeamDrawerPlayersProps {
  players: PublicUser[];
  captainId: string;
  /** When true, player names are hidden (e.g. a forfeited team). */
  hideNames?: boolean;
}

/**
 * TeamDrawerPlayers - Displays team players with avatars in drawer
 */
export function TeamDrawerPlayers({
  players,
  captainId,
  hideNames = false,
}: TeamDrawerPlayersProps) {
  const t = useTranslations();

  return (
    <div className="space-y-2">
      {players.map((player) => {
        const displayName = hideNames
          ? t("teams.forfeit.hiddenPlayer")
          : player.name;
        return (
        <div key={player.id} className="flex items-center gap-2.5">
          {/* Player avatar */}
          <div className="relative">
            <OptimizedAvatar
              src={hideNames ? undefined : player.photoURL}
              className={`w-12 h-12 ${
                captainId === player.id
                  ? "ring-2 ring-amber-500/40"
                  : "ring-1 ring-white/10"
              }`}
              name={displayName}
              optimizedSize={96}
            />
            {/* Captain crown */}
            {captainId === player.id && (
              <div className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center">
                <span className="text-[8px]">👑</span>
              </div>
            )}
          </div>

          {/* Player info */}
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium text-white/90 truncate">
              {displayName}
            </span>
            {captainId === player.id && (
              <span className="text-xs text-amber-400/80 uppercase tracking-wide">
                {t("teams.captain")}
              </span>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
}
